import { useMutation, useQueryClient } from "@tanstack/react-query";
import GlobalAppAPI from "../../api/GlobalAppAPI";
import { newToastEvent, updateToastEvent } from "../../../utils/ToastUtil";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import { InstallAppPayload } from "./useInstallGlobalApp";
import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { AppConfirm } from "../../../utils/PopupUtil";

interface Props {
  payload: InstallAppPayload;
  onSuccess?: () => void;
}

/**
 * Adds a Global App to the current workspace
 * Used when a Global App is installed on a cloud, but doesn't appear in Workspace.
 */
const useAddGlobalAppToWorkspace = () => {
  const queryClient = useQueryClient();
  const workspaceID = useAtomValue(workspaceIdAtom);

  const addGlobalAppToWorkspace = useMutation({
    mutationFn: async ({ payload }: Props) => {
      newToastEvent(
        "success",
        "Adding app to workspace...",
        "AddingGlobalApp",
        {
          loading: true,
          autoClose: false,
        }
      );

      return GlobalAppAPI.addGlobalAppToWorkspace(payload);
    },
    onSuccess: (response, { onSuccess }) => {
      queryClient.invalidateQueries({
        queryKey: ["workspace", workspaceID],
      });

      AppConfirm({
        title: "Success!",
        icon: "success",
        html: `Successfully added app to your workspace!`,
        confirmButtonText: "OK",
        confirmButtonColor: "var(--primary)",
      });

      updateToastEvent(
        "success",
        `Successfully added app to your workspace!`,
        "AddingGlobalApp",
        {
          loading: false,
          autoClose: true,
        }
      );

      callFunctionIfExists(onSuccess);
    },
    onError: (error) => {
      AppConfirm({
        title: "Error",
        html: `Failed to add app to your workspace! <br/> ${getErrorMessage(
          error
        )}`,
        confirmButtonText: "OK",
      });

      updateToastEvent(
        "error",
        `Failed to add app to your workspace! ${getErrorMessage(error)}`,
        "AddingGlobalApp",
        {
          loading: false,
          autoClose: true,
        }
      );
    },
  });

  return {
    addGlobalAppToWorkspace: addGlobalAppToWorkspace.mutate,
    addingApp: addGlobalAppToWorkspace.isPending,
  };
};

export default useAddGlobalAppToWorkspace;
